import {
  useLocalBusinessConsent,
  storedBusinessRequest,
  persistBusinessRequest,
  isLocalConfirmationRequired,
  isDefiniteBusinessRejection,
} from '../../../hooks/useLocalBusinessConsent';
import type { AdjustMemberLedgerPointsDto, OpManualConfiguration } from '@pokerroom/types';
import { ManualOpForm } from './ManualOpForm';
import { REFERRAL_OP_ASSET_TYPE_ID } from '@pokerroom/types';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberPointsHistoryDto, MemberPointsKind } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { safeFormatDate } from '../../../lib/format';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { errorMessage } from './errorMessage';
import { LedgerAdjustForm } from './LedgerAdjustForm';
import { LedgerHistoryPager } from './LedgerHistoryPager';
import { formatPoints } from './ledgerTabs';

const PAGE_SIZE = 20;

/** One points ledger: 選手點數, a 限時 month or Z點. */
export function PointsLedgerTab(props: Parameters<typeof ScopedPointsLedgerTab>[0]) {
  const scope = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
      props.memberId,
      props.assetTypeId ?? props.kind,
      props.period,
    ]),
  );
  return <ScopedPointsLedgerTab key={scope} {...props} />;
}
function ScopedPointsLedgerTab({
  memberId,
  kind,
  assetTypeId,
  unit,
  enabled = true,
  period,
  balance,
  expiresAt,
  expired,
  deductOnly,
  opManualConfiguration,
  memberLocked,
  summaryUnavailable,
}: {
  memberId: string;
  opManualConfiguration?: OpManualConfiguration;
  memberLocked?: boolean;
  summaryUnavailable?: boolean;
  kind?: MemberPointsKind;
  assetTypeId?: string;
  unit?: 'WHOLE' | 'CENTS';
  enabled?: boolean;
  period?: string;
  balance: string;
  expiresAt?: string;
  expired?: boolean;
  /** A former Z客 can only have its remaining Z點 deducted. */
  deductOnly?: boolean;
}) {
  const { t } = useTranslation('member');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const actor = useAuthStore((s) => s.user?.id);
  const revision = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const canAdjust = useAuthStore((s) => s.allowedActions?.includes('member:adjust_points'));
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const localBusiness = useLocalBusinessConsent();
  const storageKey = `member-points:v2:${actor}:${venue}:${memberId}:${assetTypeId ?? kind}:${period ?? ''}`;
  const [recovery] = useState(() => {
    try {
      const stored = storedBusinessRequest<AdjustMemberLedgerPointsDto>(storageKey);
      const body = stored?.body;
      if (
        body &&
        (!body.operationKey ||
          !['GRANT', 'DEDUCT'].includes(body.direction) ||
          typeof body.amount !== 'string' ||
          typeof body.note !== 'string')
      )
        return { invalid: true, body: null };
      return { invalid: false, body: body ?? null };
    } catch {
      return { invalid: true, body: null };
    }
  });
  const [pendingRequest, setPendingRequest] = useState(recovery.body);

  const { data, isError, isPending } = useQuery<MemberPointsHistoryDto>({
    queryKey: [
      'member-ledger',
      memberId,
      assetTypeId ?? kind,
      period ?? null,
      page,
      actor,
      venue,
      revision,
    ],
    queryFn: () =>
      api.get(`/members/${memberId}/points`, {
        params: { kind, assetTypeId, period, page, limit: PAGE_SIZE },
      }),
  });

  const adjust = useMutation({
    mutationFn: async (input: {
      operation: string;
      amount: string;
      note: string;
      operationKey?: string;
    }) => {
      let body: AdjustMemberLedgerPointsDto = pendingRequest ?? {
        kind,
        assetTypeId,
        period,
        operationKey: input.operationKey!,
        direction: input.operation as 'GRANT' | 'DEDUCT',
        amount: input.amount,
        note: input.note,
      };
      const stored = storedBusinessRequest<AdjustMemberLedgerPointsDto>(storageKey);
      if (!pendingRequest || stored?.amendable) {
        const consent = await localBusiness.ask([memberId], 'POINTS');
        if (!consent) throw Error(localBusiness.cancelledMessage);
        body = { ...body, localInitializationConsent: consent };
      }
      persistBusinessRequest(storageKey, body);
      setPendingRequest(body);
      try {
        return await api.post(`/members/${memberId}/points`, body);
      } catch (error) {
        if (isLocalConfirmationRequired(error)) {
          persistBusinessRequest(storageKey, body, 'KNOWN_REJECTED');
          throw Error(localBusiness.reconfirmMessage);
        }
        if (!pendingRequest && isDefiniteBusinessRejection(error, 'POINTS')) {
          sessionStorage.removeItem(storageKey);
          setPendingRequest(null);
        }
        throw error;
      }
    },
    onSuccess: () => {
      sessionStorage.removeItem(storageKey);
      setPendingRequest(null);
      setError('');
      setPage(1);
      void qc.invalidateQueries({ queryKey: ['member-ledger', memberId] });
      void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
      void qc.invalidateQueries({ queryKey: ['daily-ledgers'] });
    },
    onError: (failure) => setError(errorMessage(failure, t('ledgerLoadFailed'))),
  });

  const manualOp =
    assetTypeId === REFERRAL_OP_ASSET_TYPE_ID ? (
      <ManualOpForm
        memberId={memberId}
        enabledForNew={enabled && !expired}
        configuration={opManualConfiguration}
        memberLocked={memberLocked}
        queryUnavailable={summaryUnavailable || isError || isPending}
      />
    ) : null;
  if (isError)
    return (
      <>
        {manualOp}
        <p role="alert">{t('ledgerLoadFailed')}</p>
      </>
    );
  if (isPending)
    return (
      <>
        {manualOp}
        <p role="status">{tc('loading')}</p>
      </>
    );
  return (
    <div className="space-y-4">
      {localBusiness.confirmation}
      {recovery.invalid && <p role="alert">{t('manualOp.storageUnavailable')}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="bg-base-300 rounded-lg p-4">
          <p className="text-xs text-base-content/60 mb-1">{t('ledgerBalance')}</p>
          <p
            className={`text-2xl font-bold ${Number(balance) < 0 ? 'text-error' : 'text-primary'}`}
          >
            {formatPoints(balance)} <span className="text-sm">{t('pointsUnit')}</span>
            {Number(balance) < 0 && <span className="ml-2 text-sm">{t('ledgerOwed')}</span>}
          </p>
        </div>
        {expiresAt && (
          <div className="bg-base-300 rounded-lg p-4">
            <p className="text-xs text-base-content/60 mb-1">{t('ledgerExpiresAt')}</p>
            <p className={`text-lg font-semibold ${expired ? 'text-error' : 'text-base-content'}`}>
              {safeFormatDate(expiresAt, (d) => d.toLocaleDateString())}
              {expired && <span className="ml-2 text-sm">{t('ledgerExpired')}</span>}
            </p>
          </div>
        )}
      </div>

      {manualOp}
      {canAdjust &&
        !recovery.invalid &&
        (pendingRequest || (enabled && !expired)) &&
        assetTypeId !== REFERRAL_OP_ASSET_TYPE_ID && (
          <LedgerAdjustForm
            key={pendingRequest?.operationKey ?? 'new'}
            recovery={
              pendingRequest
                ? {
                    operation: pendingRequest.direction,
                    amount: pendingRequest.amount,
                    note: pendingRequest.note,
                    operationKey: pendingRequest.operationKey,
                  }
                : undefined
            }
            operations={[
              ...(deductOnly ? [] : [{ value: 'GRANT', label: t('ledgerGrant') }]),
              { value: 'DEDUCT', label: t('ledgerDeduct') },
            ]}
            amountLabel={t('ledgerAmountPoints')}
            step={unit === 'CENTS' || kind === 'Z' ? '0.01' : '1'}
            pending={adjust.isPending}
            error={error}
            keyed={{
              scope: `${memberId}:${assetTypeId ?? kind}:${period ?? ''}`,
              reload: () =>
                qc.refetchQueries(
                  { queryKey: ['member-ledger', memberId] },
                  { throwOnError: true },
                ),
            }}
            onSubmit={(input) => adjust.mutateAsync(input)}
          />
        )}
      {expired && <p className="text-sm text-base-content/60">{t('ledgerExpiredNote')}</p>}

      <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead className="bg-base-300">
            <tr>
              <th className="text-left px-4 py-2">{t('ledgerCreatedAt')}</th>
              <th className="text-left px-4 py-2">{t('ledgerType')}</th>
              <th className="text-left px-4 py-2">{t('ledgerDelta')}</th>
              <th className="text-left px-4 py-2">{t('ledgerNote')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {data?.rows.map((row) => (
              <tr key={row.id} data-ledger-id={row.id}>
                <td className="px-4 py-2 text-base-content/60 text-xs whitespace-nowrap">
                  {safeFormatDate(row.createdAt, (d) => d.toLocaleString())}
                </td>
                <td className="px-4 py-2">
                  {t(`ledgerSource_${row.source}`)}
                  {row.actorName && (
                    <span className="block text-xs text-base-content/70">{row.actorName}</span>
                  )}
                </td>
                <td
                  className={`px-4 py-2 font-mono font-medium ${Number(row.delta) < 0 ? 'text-error' : 'text-success'}`}
                >
                  {Number(row.delta) > 0 ? '+' : ''}
                  {formatPoints(row.delta)}
                </td>
                <td className="px-4 py-2 [overflow-wrap:anywhere]">{row.note}</td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-base-content/50">
                  {t('ledgerEmpty')}
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-error">
                  {t('ledgerLoadFailed')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {data && (
          <LedgerHistoryPager
            page={data.page}
            limit={data.limit}
            total={data.total}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  );
}
